import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'nzassa-offline-queue';
const STORE_NAME = 'pending-requests';
const DB_VERSION = 1;

export interface PendingRequest {
  id?: number;
  url: string;
  method: string;
  body: string | null;
  headers: Record<string, string>;
  timestamp: number;
  retryCount: number;
}

type QueueChangeListener = () => void;

let dbPromise: Promise<IDBPDatabase> | null = null;
const listeners = new Set<QueueChangeListener>();

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
            autoIncrement: true,
          });
        }
      },
    });
  }
  return dbPromise;
}

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

/** Subscribe to queue changes (add/remove/clear). Returns unsubscribe function. */
export function subscribe(listener: QueueChangeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Add a failed request to the offline queue. */
export async function add(
  request: Omit<PendingRequest, 'id' | 'timestamp' | 'retryCount'>,
): Promise<number> {
  const db = await getDb();
  const entry: PendingRequest = {
    ...request,
    timestamp: Date.now(),
    retryCount: 0,
  };
  const id = (await db.add(STORE_NAME, entry)) as number;
  notifyListeners();
  return id;
}

/** Get all pending requests ordered by insertion (FIFO). */
export async function getAll(): Promise<PendingRequest[]> {
  const db = await getDb();
  return db.getAll(STORE_NAME);
}

/** Remove a single request by id. */
export async function remove(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_NAME, id);
  notifyListeners();
}

/** Update a pending request (e.g. increment retryCount). */
export async function update(
  id: number,
  data: Partial<PendingRequest>,
): Promise<void> {
  const db = await getDb();
  const existing = await db.get(STORE_NAME, id);
  if (existing) {
    await db.put(STORE_NAME, { ...existing, ...data, id });
    notifyListeners();
  }
}

/** Clear all pending requests. */
export async function clear(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE_NAME);
  notifyListeners();
}

/** Get the number of pending requests. */
export async function size(): Promise<number> {
  const db = await getDb();
  return db.count(STORE_NAME);
}
